import type { ReactElement } from 'react';
import { Menu } from '@base-ui/react/menu';
import { Tooltip } from '@base-ui/react/tooltip';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  RiBookOpenLine,
  RiBuilding2Line,
  RiCheckLine,
  RiKeyboardLine,
  RiPaintBrushLine,
  RiSettings4Line,
  RiSidebarUnfoldLine,
  RiWaterFlashLine,
} from '@remixicon/react';
import type { AccessibleTenant } from '@atnt/shared';
import { cn } from '@/shared/lib/cn';
import { NamedIcon } from './icons';
import { isPlatformOrOrgAdmin, splitTenantPath, visibleGroups, type VisibleGroup } from './nav';
import { sectionsQuery } from './sections';
import { isUnder, rowFocus, SidebarEdge, tenantsByOrganization } from './sidebar-parts';
import { dp, useMe } from './use-me';
import { menuPopupClass, UserMenu } from './UserMenu';

/**
 * Collapsed sidebar (not designed — 01-design-system §9 Q7 proposal): a 64 px rail with the "Suv omborlar" switcher,
 * the current tenant's group chips (hover/click → flyout with its pages), global items, the account menu and
 * "Menyuni ochish". Same paddings and row heights as the full sidebar.
 */
export function SidebarRail({ tenant, onExpand }: { tenant?: AccessibleTenant; onExpand(): void }) {
  const { t } = useTranslation('shell');
  const me = useMe();
  const pathname = useLocation({ select: (l) => l.pathname });
  const route = splitTenantPath(pathname);
  const here = tenant && route?.slug === tenant.slug ? route.rest : null;
  const groups = tenant ? visibleGroups(tenant.permissions) : [];

  return (
    <Tooltip.Provider delay={300}>
      <aside className="relative flex h-full w-64 shrink-0 flex-col items-center gap-20 bg-sidebar px-8 py-20">
        <SidebarEdge />
        <div className="flex h-45 shrink-0 items-center justify-center">
          <img src="/logo-uge.webp" alt="O'zbekgidroenergo" className="w-48 object-contain" />
        </div>

        <div className="flex min-h-0 w-48 flex-1 flex-col justify-between gap-8">
          <nav aria-label={t('nav.label')} className="flex min-h-0 flex-col items-center gap-6 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <TenantSwitcher current={tenant} />
            {groups.map((g) => (
              <GroupFlyout key={g.id} group={g} tenant={tenant!} here={here} />
            ))}
            {groups.length > 0 && <span aria-hidden className="my-2 h-px w-32 shrink-0 bg-line" />}
            {isPlatformOrOrgAdmin(me) && (
              <RailTip label={t('nav.organizations')}>
                <Link to="/admin" aria-label={t('nav.organizations')} className={railCell(/^\/admin(\/|$)/.test(pathname), true)}>
                  <RiBuilding2Line aria-hidden className="size-20 text-icon" />
                </Link>
              </RailTip>
            )}
            <SettingsFlyout active={/^\/settings(\/|$)/.test(pathname)} pathname={pathname} />
            <RailTip label={t('nav.guide')}>
              <Link to="/guide" aria-label={t('nav.guide')} className={railCell(/^\/guide(\/|$)/.test(pathname), true)}>
                <RiBookOpenLine aria-hidden className="size-20 text-icon" />
              </Link>
            </RailTip>
          </nav>
          <div className="flex justify-center">
            <UserMenu tenant={tenant} compact />
          </div>
        </div>

        <RailTip label={t('nav.expand')}>
          <button type="button" onClick={onExpand} aria-label={t('nav.expand')} className={cn('flex h-32 w-48 shrink-0 items-center justify-center rounded-4 hover:bg-hover', rowFocus)}>
            <RiSidebarUnfoldLine aria-hidden className="size-20 text-icon" />
          </button>
        </RailTip>
      </aside>
    </Tooltip.Provider>
  );
}

const railCell = (active: boolean, dimmed = false) =>
  cn(
    'flex h-32 w-48 shrink-0 items-center justify-center rounded-4',
    active ? 'bg-selected' : cn('hover:bg-hover data-popup-open:bg-hover', dimmed && 'opacity-50 hover:opacity-100 data-popup-open:opacity-100'),
    rowFocus,
  );

/** Plain text tooltip (`glass-tooltip` r8, padding 6/10, 400 12/16 — §6.10 / Q14). */
export function RailTip({ label, children }: { label: string; children: ReactElement<Record<string, unknown>> }) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger render={children} />
      <Tooltip.Portal>
        <Tooltip.Positioner side="right" sideOffset={() => dp(10)} className="z-90">
          <Tooltip.Popup className="glass-tooltip rounded-8 px-10 py-6 text-xs whitespace-nowrap text-fg transition-opacity duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0">
            {label}
          </Tooltip.Popup>
        </Tooltip.Positioner>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

const flyoutItem =
  'flex h-32 cursor-pointer items-center gap-5 rounded-4 px-6 text-sm text-fg-nav outline-none select-none data-highlighted:bg-hover';

/** "Suv omborlar" in the rail: a menu of accessible tenants (grouped by organization). */
function TenantSwitcher({ current }: { current?: AccessibleTenant }) {
  const { t } = useTranslation('shell');
  const me = useMe();
  const orgs = tenantsByOrganization(me);
  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label={t('nav.reservoirs')}
        className={cn('flex h-36 w-48 shrink-0 items-center justify-center rounded-4 bg-accent', rowFocus)}
      >
        <RiWaterFlashLine aria-hidden className="size-20 text-icon" />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner side="right" align="start" sideOffset={() => dp(10)} className="z-50 outline-none">
          <Menu.Popup className={cn(menuPopupClass, 'max-h-(--available-height) w-240 overflow-y-auto')}>
            <p className="px-6 pt-4 pb-6 t-caps">{t('nav.reservoirs')}</p>
            {me.tenants.length === 0 && <p className="px-6 pb-6 t-field text-fg-tertiary">{t('nav.noTenants')}</p>}
            {orgs.map((o) => (
              <Menu.Group key={o.id}>
                {orgs.length > 1 && <Menu.GroupLabel className="truncate px-6 pt-8 pb-4 t-caps">{o.name}</Menu.GroupLabel>}
                {o.tenants.map((x) => (
                  <Menu.LinkItem key={x.id} render={<Link to="/t/$tenantSlug" params={{ tenantSlug: x.slug }} />} className={flyoutItem}>
                    <span className={cn('min-w-0 flex-1 truncate', x.id === current?.id && 'font-semibold')}>{x.name}</span>
                    {x.id === current?.id && <RiCheckLine aria-hidden className="size-16 shrink-0 text-accent-2" />}
                  </Menu.LinkItem>
                ))}
              </Menu.Group>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

/** A module group chip; hover or click opens its pages to the right. */
function GroupFlyout({ group, tenant, here }: { group: VisibleGroup; tenant: AccessibleTenant; here: string | null }) {
  const { t } = useTranslation('shell');
  const { data: sections } = useQuery({ ...sectionsQuery(tenant.id), enabled: group.sections });
  const active =
    group.leaves.some((l) => isUnder(here, l.segment)) || (group.sections && (sections ?? []).some((s) => isUnder(here, `sections/${s.id}`)));
  return (
    <Menu.Root>
      <Menu.Trigger openOnHover delay={80} aria-label={t(group.labelKey)} className={railCell(Boolean(active))}>
        <span aria-hidden className={cn('flex size-24 items-center justify-center rounded-6 text-white', group.chipClass)}>
          <group.icon className="size-16" />
        </span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner side="right" align="start" sideOffset={() => dp(10)} className="z-50 outline-none">
          <Menu.Popup className={cn(menuPopupClass, 'max-h-(--available-height) w-240 overflow-y-auto')}>
            <div className="flex h-32 items-center gap-6 px-6">
              <span aria-hidden className={cn('flex size-24 shrink-0 items-center justify-center rounded-6 text-white', group.chipClass)}>
                <group.icon className="size-16" />
              </span>
              <span className="truncate text-13 leading-20 font-medium tracking-figma text-fg-nav">{t(group.labelKey)}</span>
            </div>
            <Menu.Separator className="-mx-6 my-4 h-px bg-line" />
            {group.leaves.map((leaf) => (
              <Menu.LinkItem
                key={leaf.id}
                render={<Link to={leaf.to} params={{ tenantSlug: tenant.slug }} />}
                className={cn(flyoutItem, isUnder(here, leaf.segment) && 'bg-selected')}
              >
                <leaf.icon aria-hidden className={cn('size-16 shrink-0', leaf.iconClass)} />
                <span className="truncate">{t(leaf.labelKey)}</span>
              </Menu.LinkItem>
            ))}
            {group.sections &&
              (sections ?? []).map((s) => (
                <Menu.LinkItem
                  key={s.id}
                  render={<Link to="/t/$tenantSlug/sections/$sectionId/edit" params={{ tenantSlug: tenant.slug, sectionId: s.id }} />}
                  className={cn(flyoutItem, isUnder(here, `sections/${s.id}`) && 'bg-selected', s.hidden && 'opacity-50')}
                >
                  <span className="flex size-16 shrink-0" style={{ color: s.color }}>
                    {s.icon.kind === 'preset' ? <NamedIcon name={s.icon.name} className="size-16" /> : null}
                  </span>
                  <span className="truncate">{s.name}</span>
                </Menu.LinkItem>
              ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

function SettingsFlyout({ active, pathname }: { active: boolean; pathname: string }) {
  const { t } = useTranslation('shell');
  const items = [
    { to: '/settings/interface' as const, icon: RiPaintBrushLine, label: t('nav.interfaceSettings') },
    { to: '/settings/hotkeys' as const, icon: RiKeyboardLine, label: t('nav.hotkeys') },
  ];
  return (
    <Menu.Root>
      <Menu.Trigger openOnHover delay={80} aria-label={t('nav.settings')} className={railCell(active, true)}>
        <RiSettings4Line aria-hidden className="size-20 text-icon" />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner side="right" align="start" sideOffset={() => dp(10)} className="z-50 outline-none">
          <Menu.Popup className={cn(menuPopupClass, 'w-240')}>
            <Menu.LinkItem render={<Link to="/settings" />} className={cn(flyoutItem, 'gap-8', pathname === '/settings' && 'bg-selected')}>
              <RiSettings4Line aria-hidden className="size-20 shrink-0 text-icon" />
              <span className="truncate">{t('nav.settings')}</span>
            </Menu.LinkItem>
            <Menu.Separator className="-mx-6 my-4 h-px bg-line" />
            {items.map((it) => (
              <Menu.LinkItem key={it.to} render={<Link to={it.to} />} className={cn(flyoutItem, pathname.startsWith(it.to) && 'bg-selected')}>
                <it.icon aria-hidden className="size-16 shrink-0 text-fg-nav" />
                <span className="truncate">{it.label}</span>
              </Menu.LinkItem>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
